import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL, NAV_IDS } from "../data/content";
import { useLang } from "../i18n/LanguageContext";
import LanguageToggle from "./LanguageToggle";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-navy/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-vel flex items-center justify-between h-24">
        <button
          data-testid="nav-logo"
          onClick={() => handleNav("hero")}
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO_URL}
            alt="Velocitas"
            className="h-14 w-14 sm:h-16 sm:w-16 object-contain transition-transform duration-300 group-hover:scale-105"
          />
          <div className="hidden sm:flex flex-col items-start leading-none">
            <span className="font-heading text-2xl sm:text-3xl text-navy tracking-tight font-medium">
              {t.nav.brand}
            </span>
            <span className="font-body text-[10px] tracking-[0.22em] uppercase text-ink-soft mt-1">
              {t.nav.brandSub}
            </span>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_IDS.map((link) => (
            <button
              key={link.id}
              data-testid={link.testId}
              onClick={() => handleNav(link.id)}
              className="font-body text-sm tracking-wide text-ink-muted hover:text-navy transition-colors duration-300 link-underline"
            >
              {t.nav[link.labelKey]}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LanguageToggle />
          <button
            data-testid="nav-cta-button"
            onClick={() => handleNav("contact")}
            className="group inline-flex items-center gap-2 bg-coral hover:bg-coral-600 text-white font-body text-sm font-medium tracking-wide px-5 py-2.5 rounded-sm transition-all duration-300 hover:-translate-y-0.5"
          >
            {t.nav.cta}
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">
              →
            </span>
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <LanguageToggle variant="mobile" />
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="text-navy"
            aria-label="Toggle navigation"
          >
            {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-white border-t border-navy/10"
        >
          <div className="container-vel py-6 flex flex-col gap-5">
            {NAV_IDS.map((link) => (
              <button
                key={link.id}
                data-testid={`mobile-${link.testId}`}
                onClick={() => handleNav(link.id)}
                className="font-body text-sm tracking-wide text-ink-muted text-left hover:text-navy"
              >
                {t.nav[link.labelKey]}
              </button>
            ))}
            <button
              data-testid="mobile-nav-cta-button"
              onClick={() => handleNav("contact")}
              className="inline-flex items-center justify-center gap-2 bg-coral text-white font-body text-sm font-medium tracking-wide px-5 py-3 rounded-sm"
            >
              {t.nav.cta} →
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
